import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import type { SelectChangeEvent } from '@mui/material';
import {
  addDoraFinancialEntity,
  removeDoraFinancialEntity,
  selectDoraFinancialEntities,
  setDoraFinancialEntities,
  updateDoraFinancialEntity as updateDoraFinancialEntityState,
} from '../store/financialEntity/financialEntitySlice.ts';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import {
  createDoraFinancialEntity,
  deleteDoraFinancialEntity,
  listDoraFinancialEntities,
  updateDoraFinancialEntity,
} from '../../../api.ts';
import { DoraFinancialEntity } from '../models/doraFinancialEntity.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';
import { ENTITY_TYPE_OPTIONS } from '../constants/entityTypes.ts';
import { HIERARCHY_LEVEL_OPTIONS } from '../constants/hierarchyLevels.ts';
import { CURRENCY_OPTIONS, CURRENCIES } from '../constants/currencies.ts';
import { COUNTRIES, formatCountryDisplay } from '../constants/countries.ts';
import { useExcelTableState } from '../../../hooks/useExcelTableState.ts';
import {
  parsePasteData,
  calculateNextCell,
  applyPastedData,
  getRowBackgroundColor,
  getRowHoverColor,
} from '../../../utils/excelTableUtils.ts';

// Editable row interface for inline editing
interface EditableFinancialEntityRow {
  id?: number;
  name: string;
  description: string;
  lei: string;
  entity_name: string;
  country_code: string;
  entity_type: string;
  hierarchy_level: string;
  parent_lei: string;
  last_update_date: string;
  integration_date: string;
  deletion_date: string;
  currency_code: string;
  total_assets: string; // Use string for easier empty state handling
  isNew?: boolean;
  touched?: boolean;
}

// Helper to check if required field is empty
const isRequiredFieldEmpty = (row: EditableFinancialEntityRow, field: keyof EditableFinancialEntityRow): boolean => {
  const requiredFields: (keyof EditableFinancialEntityRow)[] = ['name'];
  if (!requiredFields.includes(field)) return false;
  const value = row[field];
  return value === '' || value === null || value === undefined;
};

// Helper to get cell background color with smart highlighting
const getCellBackgroundColor = (row: EditableFinancialEntityRow, field: keyof EditableFinancialEntityRow): string => {
  // Don't highlight untouched new rows
  if (row.isNew && !row.touched) {
    return 'transparent';
  }
  if (isRequiredFieldEmpty(row, field)) {
    // Amber/gold gradient for required empty fields
    return 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 160, 0, 0.08))';
  }
  return 'transparent';
};

export const FinancialEntitiesPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const financialEntities = useAppSelector(selectDoraFinancialEntities);
  const currentOrganization = useAppSelector(selectCurrentOrganization);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editableRows, setEditableRows] = useState<EditableFinancialEntityRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'deleted'>('all');

  const {
    focusedRowIndex,
    editingCell,
    setEditingCell,
    handleRowFocus: baseHandleRowFocus,
    handleRowBlur: baseHandleRowBlur,
    isSaving,
    markSaving,
    markSaved,
  } = useExcelTableState<EditableFinancialEntityRow>();

  // Define field order for Tab navigation
  const fieldOrder: (keyof EditableFinancialEntityRow)[] = useMemo(() => [
    'name',
    'description',
    'lei',
    'entity_name',
    'country_code',
    'entity_type',
    'hierarchy_level',
    'parent_lei',
    'last_update_date',
    'integration_date',
    'deletion_date',
    'currency_code',
    'total_assets',
  ], []);

  // Filter entities by status
  const filteredEntities = useMemo(() => {
    let filtered = [...financialEntities];

    if (statusFilter === 'active') {
      filtered = filtered.filter(e => e.deletion_date === '9999-12-31' || !e.deletion_date);
    } else if (statusFilter === 'deleted') {
      filtered = filtered.filter(e => e.deletion_date && e.deletion_date !== '9999-12-31');
    }

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [financialEntities, statusFilter]);

  // Create editable rows with empty row at end
  const rows = useMemo(() => {
    const existingRows: EditableFinancialEntityRow[] = filteredEntities.map(e => ({
      id: e.id,
      name: e.name || '',
      description: e.description || '',
      lei: e.lei || '',
      entity_name: e.entity_name || '',
      country_code: e.country_code || '',
      entity_type: e.entity_type || '',
      hierarchy_level: e.hierarchy_level || '',
      parent_lei: e.parent_lei || '',
      last_update_date: e.last_update_date || '',
      integration_date: e.integration_date || '',
      deletion_date: e.deletion_date || '9999-12-31',
      currency_code: e.currency_code || '',
      total_assets: e.total_assets !== null && e.total_assets !== undefined ? String(e.total_assets) : '',
    }));

    // Always add empty row for new entries
    const emptyRow: EditableFinancialEntityRow = {
      name: '',
      description: '',
      lei: '',
      entity_name: '',
      country_code: '',
      entity_type: '',
      hierarchy_level: '',
      parent_lei: '',
      last_update_date: '',
      integration_date: '',
      deletion_date: '9999-12-31',
      currency_code: '',
      total_assets: '',
      isNew: true,
    };

    return [...existingRows, emptyRow];
  }, [filteredEntities]);

  useEffect(() => {
    setEditableRows(rows);
  }, [rows]);

  useEffect(() => {
    const load = async () => {
      if (!currentOrganization) return;
      setLoading(true);
      setError(null);
      try {
        const items = await listDoraFinancialEntities();
        dispatch(setDoraFinancialEntities(items));
      } catch (err: any) {
        console.error('Failed to load financial entities', err);
        setError(err?.response?.data?.detail || 'Failed to load financial entities');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentOrganization, dispatch]);

  // Update row field
  const updateRow = (index: number, field: keyof EditableFinancialEntityRow, value: any) => {
    setEditableRows(prev => {
      const updated = [...prev];
      const row = updated[index];
      // Mark new rows as touched when first edited
      updated[index] = {
        ...row,
        [field]: value,
        ...(row.isNew && !row.touched ? { touched: true } : {}),
      };
      return updated;
    });
  };

  // Save row handler
  const saveRow = useCallback(async (row: EditableFinancialEntityRow, index: number) => {
    const rowKey = row.id ?? `new-${index}`;

    // Prevent duplicate saves
    if (isSaving(rowKey)) return;

    try {
      if (row.isNew) {
        // Create only if name is filled
        if (row.name.trim()) {
          markSaving(rowKey);
          const payload: Partial<DoraFinancialEntity> = {
            name: row.name.trim(),
            description: row.description.trim() || null,
            lei: row.lei.trim() || null,
            entity_name: row.entity_name.trim() || null,
            country_code: row.country_code || null,
            entity_type: row.entity_type || null,
            hierarchy_level: row.hierarchy_level || null,
            parent_lei: row.parent_lei.trim() || null,
            last_update_date: row.last_update_date || null,
            integration_date: row.integration_date || null,
            deletion_date: row.deletion_date || '9999-12-31',
            currency_code: row.currency_code || null,
            total_assets: row.total_assets ? parseFloat(row.total_assets) : null,
          };
          const created = await createDoraFinancialEntity(payload);
          dispatch(addDoraFinancialEntity(created));
        }
      } else if (row.id) {
        // Update existing
        if (row.name.trim()) {
          markSaving(rowKey);
          const payload: Partial<DoraFinancialEntity> = {
            name: row.name.trim(),
            description: row.description.trim() || null,
            lei: row.lei.trim() || null,
            entity_name: row.entity_name.trim() || null,
            country_code: row.country_code || null,
            entity_type: row.entity_type || null,
            hierarchy_level: row.hierarchy_level || null,
            parent_lei: row.parent_lei.trim() || null,
            last_update_date: row.last_update_date || null,
            integration_date: row.integration_date || null,
            deletion_date: row.deletion_date || '9999-12-31',
            currency_code: row.currency_code || null,
            total_assets: row.total_assets ? parseFloat(row.total_assets) : null,
          };
          const updated = await updateDoraFinancialEntity(row.id, payload);
          dispatch(updateDoraFinancialEntityState(updated));
        }
      }
    } catch (err: any) {
      console.error('Failed to save financial entity', err);
      setError(err?.response?.data?.detail || 'Failed to save financial entity');
    } finally {
      markSaved(rowKey);
    }
  }, [dispatch, isSaving, markSaving, markSaved]);

  // Row focus and blur handlers
  const handleRowFocus = (index: number) => {
    baseHandleRowFocus(index, editableRows, saveRow);
  };

  const handleRowBlur = () => {
    baseHandleRowBlur(editableRows, saveRow);
  };

  // Tab navigation handler
  const handleTabNavigation = (e: React.KeyboardEvent, currentRow: number, currentField: keyof EditableFinancialEntityRow) => {
    if (e.key !== 'Tab') return;

    e.preventDefault();

    const currentFieldIndex = fieldOrder.indexOf(currentField);
    const nextCell = calculateNextCell(
      currentRow,
      currentFieldIndex,
      editableRows.length,
      fieldOrder.length,
      e.shiftKey
    );

    if (nextCell) {
      if (nextCell.row !== currentRow) {
        handleRowFocus(nextCell.row);
      }
      setEditingCell({ row: nextCell.row, field: fieldOrder[nextCell.fieldIndex] });
    }
  };

  // Paste handler
  const handlePaste = useCallback((e: React.ClipboardEvent, startRowIndex: number, startField: keyof EditableFinancialEntityRow) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    const startFieldIndex = fieldOrder.indexOf(startField);

    // Parse the pasted data
    const parsedRows = parsePasteData(pasteData);

    // Normalize parsed data
    const normalizedRows = parsedRows.map(row =>
      row.map(cell => {
        const trimmed = cell.trim();
        if (trimmed === '—' || trimmed === '-' || trimmed === '–' || trimmed === '−' || trimmed === 'N/A' || trimmed === 'n/a') {
          return '';
        }
        return cell;
      })
    );

    // Create map for foreign key lookups (lei and parent_lei can lookup from existing entities)
    const entityLEIs = financialEntities.filter(e => e.lei).map(e => ({ id: e.lei!, name: e.lei! }));
    const relatedEntitiesMap = new Map<keyof EditableFinancialEntityRow, typeof entityLEIs>();
    relatedEntitiesMap.set('lei', entityLEIs);
    relatedEntitiesMap.set('parent_lei', entityLEIs);

    // Create empty row function
    const createEmptyRow = (): EditableFinancialEntityRow => ({
      name: '',
      description: '',
      lei: '',
      entity_name: '',
      country_code: '',
      entity_type: '',
      hierarchy_level: '',
      parent_lei: '',
      last_update_date: '',
      integration_date: '',
      deletion_date: '9999-12-31',
      currency_code: '',
      total_assets: '',
      isNew: true,
      touched: true,
    });

    setEditableRows(prev => {
      const updated = applyPastedData(
        prev,
        normalizedRows,
        startRowIndex,
        startFieldIndex,
        fieldOrder,
        relatedEntitiesMap,
        createEmptyRow
      );
      // Mark affected rows as touched
      return updated.map((row, idx) => {
        if (idx >= startRowIndex && idx < startRowIndex + normalizedRows.length && row.isNew) {
          return { ...row, touched: true };
        }
        return row;
      });
    });

    // Auto-save pasted rows after delay
    setTimeout(() => {
      normalizedRows.forEach((_, rowOffset) => {
        const targetRowIndex = startRowIndex + rowOffset;
        if (targetRowIndex < editableRows.length) {
          saveRow(editableRows[targetRowIndex], targetRowIndex);
        }
      });
    }, 300);
  }, [editableRows, financialEntities, saveRow, fieldOrder]);

  // Delete handler
  const handleDelete = async (id?: number) => {
    if (!id) return;
    if (!window.confirm('Delete this financial entity?')) return;

    setError(null);

    try {
      await deleteDoraFinancialEntity(id);
      dispatch(removeDoraFinancialEntity(id));
    } catch (err: any) {
      console.error('Failed to delete financial entity', err);
      setError(err?.response?.data?.detail || 'Failed to delete financial entity');
    }
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Financial Entities (B_01.02)</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {loading && <CircularProgress size={20} />}
            </Stack>
          </Stack>

          {/* Status Filter */}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ alignSelf: 'center' }}>
              Status:
            </Typography>
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(_, value) => value && setStatusFilter(value)}
              size="small"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="active">Active</ToggleButton>
              <ToggleButton value="deleted">Deleted</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 150 }}>Name*</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 200 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 180 }}>LEI</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 150 }}>Entity Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 120 }}>Country</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 200 }}>Entity Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 250 }}>Hierarchy Level</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 180 }}>Parent LEI</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 130 }}>Last Update</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 130 }}>Integration Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 130 }}>Deletion Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 100 }}>Currency</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', minWidth: 120 }}>Total Assets</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 60 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editableRows.map((row, index) => {
                  const isFocused = focusedRowIndex === index;
                  const isEvenRow = index % 2 === 0;

                  return (
                    <TableRow
                      key={row.id ?? `new-${index}`}
                      sx={{
                        bgcolor: getRowBackgroundColor(isFocused, row.isNew || false, isEvenRow),
                        '&:hover': {
                          bgcolor: getRowHoverColor(isFocused),
                        },
                        transition: 'background-color 0.15s ease',
                      }}
                    >
                      {/* Name column - Required */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'name' ? (
                          <TextField
                            value={row.name}
                            onChange={(e) => updateRow(index, 'name', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'name')}
                            onPaste={(e) => handlePaste(e, index, 'name')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder={row.isNew ? 'Enter name...' : ''}
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'name' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'name'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.name || (row.isNew ? <span style={{ color: '#999' }}>Enter name...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Description column */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'description' ? (
                          <TextField
                            value={row.description}
                            onChange={(e) => updateRow(index, 'description', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'description')}
                            onPaste={(e) => handlePaste(e, index, 'description')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder={row.isNew ? 'Enter description...' : ''}
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'description' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                            }}
                          >
                            {row.description || (row.isNew ? <span style={{ color: '#999' }}>Enter description...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* LEI column */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'lei' ? (
                          <TextField
                            value={row.lei}
                            onChange={(e) => updateRow(index, 'lei', e.target.value.slice(0, 20))}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'lei')}
                            onPaste={(e) => handlePaste(e, index, 'lei')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder={row.isNew ? '20 char LEI...' : ''}
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1, fontFamily: 'monospace' } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'lei' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                              fontFamily: 'monospace',
                            }}
                          >
                            {row.lei || (row.isNew ? <span style={{ color: '#999' }}>20 char LEI...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Entity Name column */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'entity_name' ? (
                          <TextField
                            value={row.entity_name}
                            onChange={(e) => updateRow(index, 'entity_name', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'entity_name')}
                            onPaste={(e) => handlePaste(e, index, 'entity_name')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder={row.isNew ? 'Entity name...' : ''}
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'entity_name' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                            }}
                          >
                            {row.entity_name || (row.isNew ? <span style={{ color: '#999' }}>Entity name...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Country column */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'country_code' ? (
                          <Autocomplete
                            value={row.country_code || null}
                            onChange={(_, newValue) => {
                              updateRow(index, 'country_code', newValue || '');
                            }}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'country_code')}
                            options={COUNTRIES.map(c => c.code)}
                            getOptionLabel={(option) => {
                              const country = COUNTRIES.find(c => c.code === option);
                              return country ? `${country.flag} ${country.name}` : option;
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder="Select country..."
                                InputProps={{ ...params.InputProps, disableUnderline: true }}
                                sx={{ '& input': { p: 1 } }}
                                autoFocus
                              />
                            )}
                            size="small"
                            freeSolo
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'country_code' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                            }}
                          >
                            {row.country_code ? formatCountryDisplay(row.country_code) : (row.isNew ? <span style={{ color: '#999' }}>Select...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Entity Type column */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'entity_type' ? (
                          <FormControl fullWidth size="small" variant="standard">
                            <Select
                              value={row.entity_type || ''}
                              onChange={(e: SelectChangeEvent<string>) => {
                                updateRow(index, 'entity_type', e.target.value);
                              }}
                              onBlur={() => {
                                handleRowBlur();
                                setEditingCell(null);
                              }}
                              onKeyDown={(e) => handleTabNavigation(e, index, 'entity_type')}
                              displayEmpty
                              disableUnderline
                              sx={{ '& .MuiSelect-select': { p: 1 } }}
                            >
                              {row.isNew && <MenuItem value=""><em>Select...</em></MenuItem>}
                              {ENTITY_TYPE_OPTIONS.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'entity_type' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                            }}
                          >
                            {row.entity_type || (row.isNew ? <span style={{ color: '#999' }}>Select...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Hierarchy Level column */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'hierarchy_level' ? (
                          <FormControl fullWidth size="small" variant="standard">
                            <Select
                              value={row.hierarchy_level || ''}
                              onChange={(e: SelectChangeEvent<string>) => {
                                updateRow(index, 'hierarchy_level', e.target.value);
                              }}
                              onBlur={() => {
                                handleRowBlur();
                                setEditingCell(null);
                              }}
                              onKeyDown={(e) => handleTabNavigation(e, index, 'hierarchy_level')}
                              displayEmpty
                              disableUnderline
                              sx={{ '& .MuiSelect-select': { p: 1, fontSize: '0.875rem' } }}
                            >
                              {row.isNew && <MenuItem value=""><em>Select...</em></MenuItem>}
                              {HIERARCHY_LEVEL_OPTIONS.map(opt => (
                                <MenuItem key={opt} value={opt} sx={{ fontSize: '0.875rem' }}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'hierarchy_level' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                              fontSize: '0.875rem',
                            }}
                          >
                            {row.hierarchy_level || (row.isNew ? <span style={{ color: '#999' }}>Select...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Parent LEI column */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'parent_lei' ? (
                          <Autocomplete
                            value={row.parent_lei || null}
                            onChange={(_, newValue) => {
                              updateRow(index, 'parent_lei', newValue ? newValue.slice(0, 20) : '');
                            }}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'parent_lei')}
                            options={financialEntities.filter(e => e.lei && e.id !== row.id).map(e => e.lei!)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder="Parent LEI..."
                                InputProps={{ ...params.InputProps, disableUnderline: true }}
                                sx={{ '& input': { p: 1, fontFamily: 'monospace' } }}
                                autoFocus
                              />
                            )}
                            size="small"
                            freeSolo
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'parent_lei' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                              fontFamily: 'monospace',
                            }}
                          >
                            {row.parent_lei || (row.isNew ? <span style={{ color: '#999' }}>Parent LEI...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Last Update Date column */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'last_update_date' ? (
                          <TextField
                            type="date"
                            value={row.last_update_date}
                            onChange={(e) => updateRow(index, 'last_update_date', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'last_update_date')}
                            size="small"
                            fullWidth
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'last_update_date' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                            }}
                          >
                            {row.last_update_date || (row.isNew ? <span style={{ color: '#999' }}>Select date...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Integration Date column */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'integration_date' ? (
                          <TextField
                            type="date"
                            value={row.integration_date}
                            onChange={(e) => updateRow(index, 'integration_date', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'integration_date')}
                            size="small"
                            fullWidth
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'integration_date' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                            }}
                          >
                            {row.integration_date || (row.isNew ? <span style={{ color: '#999' }}>Select date...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Deletion Date column */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'deletion_date' ? (
                          <TextField
                            type="date"
                            value={row.deletion_date === '9999-12-31' ? '' : row.deletion_date}
                            onChange={(e) => updateRow(index, 'deletion_date', e.target.value || '9999-12-31')}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'deletion_date')}
                            size="small"
                            fullWidth
                            variant="standard"
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            placeholder="Active"
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'deletion_date' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                            }}
                          >
                            {row.deletion_date === '9999-12-31' || !row.deletion_date ? (
                              <span style={{ color: '#4caf50' }}>Active</span>
                            ) : (
                              row.deletion_date
                            )}
                          </div>
                        )}
                      </TableCell>

                      {/* Currency column */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'currency_code' ? (
                          <Autocomplete
                            value={row.currency_code || null}
                            onChange={(_, newValue) => {
                              updateRow(index, 'currency_code', newValue || '');
                            }}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'currency_code')}
                            options={CURRENCY_OPTIONS}
                            getOptionLabel={(option) => {
                              const currency = CURRENCIES.find(c => c.code === option);
                              return currency ? `${currency.code} - ${currency.name}` : option;
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder="Currency..."
                                InputProps={{ ...params.InputProps, disableUnderline: true }}
                                sx={{ '& input': { p: 1 } }}
                                autoFocus
                              />
                            )}
                            size="small"
                            freeSolo
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'currency_code' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                            }}
                          >
                            {row.currency_code || (row.isNew ? <span style={{ color: '#999' }}>Select...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Total Assets column */}
                      <TableCell>
                        {editingCell?.row === index && editingCell?.field === 'total_assets' ? (
                          <TextField
                            type="number"
                            value={row.total_assets}
                            onChange={(e) => updateRow(index, 'total_assets', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'total_assets')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder={row.isNew ? 'Amount...' : ''}
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'total_assets' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                              textAlign: 'right',
                            }}
                          >
                            {row.total_assets ? parseFloat(row.total_assets).toLocaleString() : (row.isNew ? <span style={{ color: '#999' }}>Amount...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Actions column */}
                      <TableCell align="right">
                        {!row.isNew && (
                          <IconButton
                            onClick={() => handleDelete(row.id)}
                            size="small"
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default FinancialEntitiesPanel;
